<script setup>
    import { updateHead } from '@/composables/helpers/updateHead';
    import { useArchivesStore } from '@/stores/archives';
    import { useArchivesNeighborhoodsStore } from '@/stores/archives-neighborhoods';
    import { storeToRefs } from 'pinia';

    const route = useRoute();
    const frontEnd = useRuntimeConfig().public.clientFrontend;
    const showCredit = ref(false);
    const { neighborhoodsMap } = storeToRefs(useArchivesNeighborhoodsStore());

    // Props
    const { pathId } = defineProps({
        pathId: {
            required: true,
            type: String,
            default: '',
        },
    });

    const { sitesData } = storeToRefs(useArchivesStore());
    const siteData = computed(() => sitesData.value.find((site) => site.id == pathId));

    const credits = computed(() => {
        const creditArr = [];
        if (siteData?.value?.fields?.['Image Credit']) {
            const photos = siteData?.value?.fields['Photos'];
            photos.forEach((photo) => {
                creditArr.push(siteData?.value?.fields['Image Credit']);
            });
        }
        return creditArr.reverse();
    });

    const desc = computed(() => {
        return siteData?.value?.fields?.['Web Description'] || '';
    });

    const years = computed(() => {
        const yearsData = siteData?.value?.fields?.['OHC Years of Participation'];
        return yearsData ? yearsData.sort((a, b) => a - b) : [];
    });

    const photos = computed(() => {
        const arr = siteData?.value.fields?.['Photos'];
        return arr ? arr.slice().reverse() : false;
    });

    const architect_title = computed(() => {
        const yearRegex = /\b\d{4}\b/;
        if (siteData?.value.fields?.['Architect'] && yearRegex.test(siteData?.value.fields['Architect'])) {
            return 'Architect, Year Completed';
        }
        return 'Architect';
    });

    const siteNeighborhood = computed(() => {
        const neighborhoodID = siteData?.value?.fields['OHC Neighborhood']?.[0];
        return neighborhoodID && neighborhoodsMap.value ? neighborhoodsMap.value[neighborhoodID] : false;
    });

    updateHead({
        title: siteData?.value?.fields['Name'],
        description: desc,
        path: route.path,
        image: frontEnd + '/img/ohc-share.jpg',
    });
</script>

<template>
    <section>
        <article class="site-detail archive" v-if="siteData">
            <BlocksImageryHero v-if="photos" :photos="photos" :credits="credits" />
            <div v-if="showCredit" class="credit">
                <p v-html="siteData.fields['Image Credit']"></p>
            </div>
            <div class="hd-container">
                <div class="archived-banner">This is an archived listing.</div>
                <section class="header-details container">
                    <p v-if="siteNeighborhood?.fields?.Name" class="hood">
                        {{ siteNeighborhood.fields.Name }}
                    </p>
                    <h1 class="headline-1">{{ siteData.fields['Name'] }}</h1>
                    <p class="body-book address">{{ siteData.fields['Site Address'] }}</p>
                    <p v-if="siteData.fields['Site Type']" class="site-type">
                        {{ siteTypeLabel(siteData.fields['Site Type']) }}
                    </p>
                </section>

                <section class="building-details" v-if="siteData?.fields['Web Description']">
                    <p class="details-title container">Details</p>
                    <BlocksDetailDescription class="building-description">
                        <p v-html="siteData.fields['Web Description']"></p>
                    </BlocksDetailDescription>
                </section>

                <section class="building-details" v-if="siteData?.fields['Architect']">
                    <p class="details-title container" v-html="architect_title"></p>
                    <BlocksDetailDescription class="building-description">
                        <p v-html="siteData.fields['Architect']"></p>
                    </BlocksDetailDescription>
                </section>

                <section class="building-details" v-if="years.length && years.length > 0">
                    <p class="details-title container">Years Participated</p>
                    <BlocksDetailDescription>
                        <template v-for="(year, index) in years">
                            <span v-if="index === years.length - 1 && years.length > 2"> and </span>
                            <span v-else-if="index > 0">, </span>
                            <NuxtLink :to="`/archive/${year}`" class="link year-link">{{ year }}</NuxtLink>
                        </template>
                    </BlocksDetailDescription>
                </section>

                <GlobalSocialMedia
                    v-if="siteData.fields"
                    styleClass=""
                    :facebook="siteData.fields['Facebook']"
                    :twitter="siteData.fields['Twitter']"
                    :instagram="siteData.fields['Instagram']"
                    :website="siteData.fields['Website']" />

            </div>
        </article>
    </section>
</template>

<style lang="scss">
    .site-detail.archive {
        position: relative;

        .header-details {
            margin-top: 18px;
        }

        .hood {
            margin: 6px 0;
            font-size: 12px;
            font-weight: 800;
            line-height: 10px;
            letter-spacing: 0.72px;
            text-transform: uppercase;
        }

        .years {
            margin-top: 0px;
            display: flex;
            flex-direction: column;

            ul {
                display: inline;
            }

            li {
                display: inline;
                &:after {
                    content: ', ';
                }
                &:last-child::after {
                    content: '';
                }
            }
        }

        .year-link {
            display: inline-block;
            color: $red;
            border-bottom: 1px solid $red;
            &:hover {
                color: $turquoise;
                border-bottom-color: $turquoise;
            }
        }

        .site-type {
            padding: 6px 10px 4px;
            font-size: 12px;
            font-weight: 800;
            line-height: 1em;
            letter-spacing: 0.96px;
            text-transform: uppercase;
            background-color: $red;
            display: inline-block;
            margin-top: 12px;
        }

        .archived-banner {
            background-color: $red;
            padding: 16px;
            margin-top: 18px;
            color: $white;
        }

        .actions {
            margin-top: 32px;
        }

        .description {
            margin: 36px 0;
        }

        .gray-tag {
            margin: 0 0 16px;
        }

        .address {
            margin-top: 4px;
        }

        .flex-row {
            display: flex;
            align-items: center;
        }

        .small-heavy {
            text-transform: uppercase;
        }

        .body-book.heavy,
        .small-book.heavy {
            font-weight: 800;
        }

        .building-details {
            margin-top: 32px;

            .details-title {
                text-transform: uppercase;
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        .neighborhood {
            margin-top: 60px;

            .subhead-2 {
                margin-bottom: 20px;
            }
        }

        .map-toggle {
            position: absolute;
            top: 16px;
            z-index: 57;
            right: 15px;
        }

        .image-wrapper {
            position: relative;
        }

        .site-instructions {
            section {
                padding-top: 10px;
            }

            .title {
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        @media (min-width: $tablet) {
            // `margin: auto` had nothing to centre without a width cap, so past
            // $hd the page ran the full viewport — and .hd-container drops its
            // 16px padding at exactly that width, leaving the title and body
            // flush against the left edge. Matches Sites/single.vue
            // (Asana 1217333292818328).
            max-width: $hd;
            width: 100vw;
            margin: auto;

            .actions {
                padding-right: 10px;
            }

            .outer-wrapper:has(.map-and-carousel-container) {
                height: 500px;
            }

            .map-and-carousel-container .map-square.short {
                height: 500px;
            }

            .map-toggle.top-actions {
                top: -52px;
                right: -9px;
            }

            .outer-container {
                display: inline-block;
            }

            .container.hours {
                display: inline-block;
                width: 50%;
                padding: 0 25px;
            }

            .block-amenities {
                display: inline-block;
                margin-top: 0px;
                margin-right: 18px;
            }

            .site-meta-container {
                display: inline-block;
                // width: 66.66%;

                .building-meta {
                    max-width: none;
                    display: flex;
                    gap: 50px;
                    margin-top: 0px;

                    .heavy {
                        margin-bottom: 16px;
                        line-height: 18px;
                        font-size: 16px;
                    }
                }
            }

            .amenities-site-meta-wrapper {
                display: flex;
                margin-top: 60px;
            }
        }

        @media (min-width: $desktop) {
            .site-meta-container .building-meta {
                gap: 80px;
            }

            .block-amenities {
                margin-right: 48px;
            }

            .actions {
                padding-right: 80px;
            }
        }
    }
</style>
